'use strict';

/**
 * Utilitários e funções de detecção de navegador
 */
const utils = {
    // Verifica se o navegador é IE
    isIE: () => {
        return window.navigator.userAgent.indexOf("MSIE ") > 0 ||
            window.navigator.userAgent.indexOf("Edge") > 0 ||
            !!navigator.userAgent.match(/Trident\/7\./);
    },

    // Verifica se está online
    isOnline: () => navigator.onLine,

    /* Movimento reduzido: espelha em JS o @media (prefers-reduced-motion) de
       custom-style.css. O CSS zera transition/animation, mas não alcança movimento
       feito na mão pelo JS — o parallax reposiciona backgroundPosition a cada scroll
       e o jQuery anima com timer próprio. Consultado a cada chamada (não cacheado):
       a preferência muda em tempo de execução. */
    prefersReducedMotion: () => {
        return typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },

    // Formata o HTML para exibição de status offline
    getOfflineHtml: (id) => {
        return `<p id="${id}" class="text-center">🦷 Eita, você está Off-line 😥</p>`;
    }
};

const assetManager = {
    deployVersion: window.deployVersion || '',
    sectionBundles: window.sectionAssetBundles || {},

    bundles: {
        carousel: {
            scripts: ['/js/owl.carousel.js'],
            styles: ['/css/owl.carousel.css', '/css/owl.theme.css'],
            isReady: () => typeof $.fn.owlCarousel === 'function'
        },
        gallery: {
            scripts: ['/js/masonry.pkgd.min.js', '/js/jquery.magnific-popup.min.js'],
            styles: ['/css/magnific-popup.css'],
            isReady: () => typeof $.fn.masonry === 'function' && typeof $.fn.magnificPopup === 'function'
        },
        parallax: {
            scripts: ['/js/jquery.parallax-1.1.3.js'],
            styles: [],
            isReady: () => typeof $.fn.parallax === 'function'
        }
    },

    loadedFiles: new Map(),

    waitForAssetElement: (element, assetKey) => {
        if (!element) {
            return Promise.resolve();
        }

        if (!element.dataset || !element.dataset.assetPath) {
            return Promise.resolve();
        }

        if (element.dataset.assetLoaded === 'true') {
            return Promise.resolve();
        }

        if (element.dataset.assetLoaded === 'error') {
            return Promise.reject(new Error(`Falha anterior ao carregar asset: ${assetKey}`));
        }

        const cachedPromise = assetManager.loadedFiles.get(assetKey);
        if (cachedPromise) {
            return cachedPromise;
        }

        const elementPromise = new Promise((resolve, reject) => {
            const onLoad = () => {
                element.dataset.assetLoaded = 'true';
                resolve();
            };

            const onError = () => {
                element.dataset.assetLoaded = 'error';
                reject(new Error(`Falha ao carregar asset existente: ${assetKey}`));
            };

            element.addEventListener('load', onLoad, { once: true });
            element.addEventListener('error', onError, { once: true });
        });

        assetManager.loadedFiles.set(assetKey, elementPromise);
        return elementPromise;
    },

    normalizeSection: (sectionPage) => {
        if (!sectionPage || sectionPage === '/' || sectionPage === 'home') {
            return 'home';
        }

        return String(sectionPage).replace(/^\/+|\/+$/g, '');
    },

    versionedPath: (path) => {
        if (!path || !assetManager.deployVersion) {
            return path;
        }

        try {
            const url = new URL(path, window.location.origin);

            if (url.origin !== window.location.origin) {
                return path;
            }

            url.searchParams.set('cv', assetManager.deployVersion);

            if (path.startsWith('/')) {
                return `${url.pathname}${url.search}`;
            }

            return url.toString();
        } catch (error) {
            return path;
        }
    },

    loadScript: (src) => {
        const versionedSrc = assetManager.versionedPath(src);
        const existingScript = document.querySelector(`script[src="${versionedSrc}"], script[data-asset-path="${src}"]`);
        if (existingScript) {
            return assetManager.waitForAssetElement(existingScript, src);
        }

        const cachedPromise = assetManager.loadedFiles.get(src);
        if (cachedPromise) {
            return cachedPromise;
        }

        const scriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = versionedSrc;
            script.dataset.assetPath = src;
            script.dataset.assetLoaded = 'pending';
            script.defer = true;
            script.onload = () => {
                script.dataset.assetLoaded = 'true';
                resolve();
            };
            script.onerror = () => {
                script.dataset.assetLoaded = 'error';
                reject(new Error(`Falha ao carregar script: ${src}`));
            };
            document.body.appendChild(script);
        });

        assetManager.loadedFiles.set(src, scriptPromise);
        return scriptPromise;
    },

    loadStyle: (href) => {
        const versionedHref = assetManager.versionedPath(href);
        const existingStyle = document.querySelector(`link[rel="stylesheet"][href="${versionedHref}"], link[rel="stylesheet"][data-asset-path="${href}"]`);
        if (existingStyle) {
            return assetManager.waitForAssetElement(existingStyle, href);
        }

        const cachedPromise = assetManager.loadedFiles.get(href);
        if (cachedPromise) {
            return cachedPromise;
        }

        const stylePromise = new Promise((resolve, reject) => {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = versionedHref;
            link.dataset.assetPath = href;
            link.dataset.assetLoaded = 'pending';
            link.onload = () => {
                link.dataset.assetLoaded = 'true';
                resolve();
            };
            link.onerror = () => {
                link.dataset.assetLoaded = 'error';
                reject(new Error(`Falha ao carregar estilo: ${href}`));
            };
            document.head.appendChild(link);
        });

        assetManager.loadedFiles.set(href, stylePromise);
        return stylePromise;
    },

    waitUntilBundleReady: (bundleName, timeout = 4000) => {
        const bundle = assetManager.bundles[bundleName];

        if (!bundle || bundle.isReady()) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            const startedAt = Date.now();

            const checkBundle = () => {
                if (bundle.isReady()) {
                    resolve();
                    return;
                }

                if ((Date.now() - startedAt) >= timeout) {
                    reject(new Error(`Bundle não ficou pronto: ${bundleName}`));
                    return;
                }

                window.setTimeout(checkBundle, 30);
            };

            checkBundle();
        });
    },

    hasPlugin: (pluginName) => Boolean(window.jQuery && $.fn && typeof $.fn[pluginName] === 'function'),

    ensureBundle: async (bundleName) => {
        const bundle = assetManager.bundles[bundleName];

        if (!bundle || bundle.isReady()) {
            return;
        }

        await Promise.all(bundle.styles.map(assetManager.loadStyle));
        await Promise.all(bundle.scripts.map(assetManager.loadScript));
        await assetManager.waitUntilBundleReady(bundleName);
    },

    ensureOptionalBundle: async (bundleName) => {
        try {
            await assetManager.ensureBundle(bundleName);
            return true;
        } catch (error) {
            console.warn(`Bundle opcional indisponível: ${bundleName}`, error);
            return false;
        }
    },

    ensureForSection: async (sectionPage) => {
        const normalizedSection = assetManager.normalizeSection(sectionPage);
        const bundles = assetManager.sectionBundles[normalizedSection] || [];

        for (const bundleName of bundles) {
            await assetManager.ensureBundle(bundleName);
        }
    }
};

const initializeDicasCarousel = (items) => {
    const $carousel = $('#dicas-carousel');

    if (!$carousel.length || $carousel.hasClass('owl-loaded')) {
        return true;
    }

    if (!assetManager.hasPlugin('owlCarousel') || typeof $carousel.owlCarousel !== 'function') {
        return false;
    }

    try {
        $carousel.owlCarousel({
            items,
            pagination: true,
            paginationNumbers: true
        });

        return true;
    } catch (error) {
        // Evita quebra da navegação quando o plugin é carregado tardiamente.
        console.warn('Inicialização do carousel indisponível no momento.', error);
        return false;
    }
};

// Adiciona classe para Safari no Mac
if (navigator.userAgent.indexOf('Safari') !== -1 &&
    navigator.userAgent.indexOf('Mac') !== -1 &&
    navigator.userAgent.indexOf('Chrome') === -1) {
    $('body').addClass('safari-mac');
}

/**
 * Gerenciador de conexão
 */
const connectionManager = {
    updateStatus: (connected) => {
        const id_md = "alertinternet";
        const id_info = "fixe-offline";

        if (connected) {
            $('body').removeClass('offline');
            if ($('#' + id_md).length) alertm.hide();
            $('#' + id_info).slideUp(750, function () {
                $(this).remove();
            });
        } else {
            if (!$('body.offline').length) {
                alertm.md(
                    '<i class="fa fa-signal text-muted"></i>&nbsp;&nbsp;Conexão com a Internet',
                    `<h2 id="${id_md}" class="text-odonto">OFF-LINE</h2><p class="lead">Por gentileza, verifique sua conexão com a internet! 😱</p>`
                );

                $('body').addClass('offline').append(utils.getOfflineHtml(id_info));

                $('#' + id_info).css({
                    'display': 'none',
                    'z-index': '999',
                    'width': '100%',
                    'position': 'fixed',
                    'bottom': '0px',
                    'background': '#eee',
                    'padding': '14px'
                }).slideDown(750);
            }
        }
    },

    init: () => {
        window.addEventListener('online', () => connectionManager.updateStatus(true), false);
        window.addEventListener('offline', () => connectionManager.updateStatus(false), false);
        connectionManager.updateStatus(utils.isOnline());
    }
};

/**
 * Gerenciador de privacidade — Google Consent Mode v2.
 *
 * O default (ad_storage/analytics_storage negados) já é setado inline em
 * include/header.php, antes do GTM. Este módulo só decide se e quando manda
 * um 'consent update' explícito tanto para liberar como para revogar.
 *
 * Compat: a versão anterior gravava a string 'true' no localStorage (sem
 * distinguir aceitar/rejeitar). Tratamos isso como 'granted' pra não pedir
 * consentimento de novo de quem já tinha aceitado.
 */
const privacyManager = {
    bannerObserver: null,
    measureBanner: null,
    clearBannerMeasure() {
        this.bannerObserver?.disconnect();
        if (this.measureBanner) window.removeEventListener('resize', this.measureBanner);
        this.bannerObserver = this.measureBanner = null;
        document.documentElement.style.removeProperty('--privacidade-altura');
    },
    decision: null,
    returnFocus: null,
    readDecision: () => {
        let stored = privacyManager.decision;
        try { stored = stored || window.localStorage.getItem('privacidade'); } catch (_) { /* armazenamento bloqueado */ }
        return stored === 'true' ? 'granted' : stored;
    },
    saveDecision: (decision) => {
        privacyManager.decision = decision;
        try { window.localStorage.setItem('privacidade', decision); } catch (_) { /* mantém a decisão nesta visita */ }
    },
    applyConsent: (decisao) => {
        if (typeof gtag !== 'function') return;
        if (decisao === 'granted' || decisao === 'denied') {
            gtag('consent', 'update', {
                'ad_storage': decisao,
                'ad_user_data': decisao,
                'ad_personalization': decisao,
                'analytics_storage': decisao
            });
            if (decisao === 'granted') dicasMetricas.init();
        }
    },

    init: (reopen = false) => {
        const md = "fixe-privacidade";
        const decisao = privacyManager.readDecision();

        if (!reopen && (decisao === 'granted' || decisao === 'denied')) {
            privacyManager.clearBannerMeasure();
            // saída pelo mesmo transform (ver comentário no fim deste init): slideUp
            // animava altura e deslocava o layout. O setTimeout é rede de segurança —
            // com prefers-reduced-motion a transição é ~0 e o transitionend pode não vir.
            const $saindo = $('#' + md).addClass('privacidade-fechando');
            if ($saindo.length) {
                $saindo.one('transitionend', () => $saindo.remove());
                setTimeout(() => $saindo.remove(), 600);
            }
            privacyManager.applyConsent(decisao);
            if (privacyManager.returnFocus?.isConnected) privacyManager.returnFocus.focus({ preventScroll: true });
            privacyManager.returnFocus = null;
            return;
        }

        privacyManager.clearBannerMeasure();
        $('#' + md).remove();

        const html = `
            <div class="row">
                <div class="col-xs-12 col-md-6 col-md-offset-3 fundo-odonto-degrade well">
                    <div class="col-xs-12 col-md-8 text-white">
                        <p class="h4 text-white">É hora de cuidar da sua privacidade</p>
                        <div class="privacidade-texto">Usamos cookies para o site funcionar e personalizar sua experiência. <a href="/politica-de-privacidade" title="Política de privacidade" class="text-white">Política de privacidade</a>.</div>
                    </div>
                    <div class="col-xs-12 col-md-4 privacidade-acoes">
                        <p><button type="button" data-acao="aceitar" class="btn btn-default btn-block btn-lg">Aceitar</button></p>
                        <p><button type="button" data-acao="rejeitar" class="btn btn-link text-white btn-block">Rejeitar</button></p>
                    </div>
                </div>
            </div>`;

        const decidir = (novaDecisao) => (e) => {
            e.preventDefault();
            privacyManager.saveDecision(novaDecisao);
            if (!privacyManager.returnFocus) {
                privacyManager.returnFocus = document.getElementById('page-main');
            }
            return privacyManager.init();
        };

        /* role="region" + aria-label dão ao gate um LANDMARK nomeado: quem usa leitor
           de tela passa a encontrá-lo pela lista de regiões, em vez de tabular até a
           parada 32 de 34 (medido em 2026-08-24). Deliberadamente NÃO é role="dialog":
           dialog promete modalidade — foco preso dentro, Esc para sair — e sem focus
           trap o papel MENTE para a tecnologia assistiva, o que é pior que não ter
           papel nenhum. O trap de verdade fica pendente; o nome é honesto e já ajuda. */
        const $banner = $('body').append(`<div id="${md}" role="region" aria-label="Aviso de privacidade">${html}</div>`).find('#' + md);
        // Mantém o contato das landings acima do consentimento, inclusive com texto ampliado.
        privacyManager.measureBanner = () => {
            document.documentElement.style.setProperty('--privacidade-altura', `${Math.ceil($banner[0].getBoundingClientRect().height)}px`);
        };
        privacyManager.measureBanner();
        if (typeof ResizeObserver === 'function') {
            privacyManager.bannerObserver = new ResizeObserver(privacyManager.measureBanner);
            privacyManager.bannerObserver.observe($banner[0]);
        }
        window.addEventListener('resize', privacyManager.measureBanner);
        $banner.find('[data-acao="aceitar"]').off().on('click', decidir('granted'));
        $banner.find('[data-acao="rejeitar"]').off().on('click', decidir('denied'));
        if (reopen) $banner.find('[data-acao="aceitar"]').trigger('focus');

        // Nada de posicionar nem animar por aqui: #fixe-privacidade (css/style.css) já
        // nasce fixo no rodapé e visível, e a entrada é uma animação CSS que toca sozinha
        // ao ser inserido. O slideDown que existia aqui animava ALTURA, e altura reflui a
        // cada frame — eram 14 layout shifts (0,019 de CLS) em toda página com o banner,
        // medido em 2026-08-17. Mostrar o banner não depende mais de JS além deste append.
        return $banner;
    }
};

document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-privacy-settings]') : null;
    if (!trigger) return;
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    privacyManager.returnFocus = trigger;
    privacyManager.init(true);
});

/**
 * Efeitos de parallax
 */
const parallaxEffects = {
    /* O efeito só roda em desktop de verdade: acima de 1199px, sem ser celular/tablet e
       sem movimento reduzido. É a MESMA condição que decide baixar o plugin e aplicá-lo:
       até 20260923.11 a carga só olhava o movimento reduzido, e o celular baixava o
       jquery.parallax que nunca usaria (PageSpeed mobile de 2026-09-23). */
    deveAplicar: () => {
        return $(window).width() > 1199
            && !(device.tablet() || device.mobile())
            && !utils.prefersReducedMotion();
    },

    /* Condição única de carga do plugin — estava repetida em 4 seções. Única via de carga
       desde 20260923.12 (o bundle 'parallax' saiu de section_asset_bundles). */
    shouldLoadPlugin: () => {
        return $('.parallax-photo[data-background]').length > 0
            && !assetManager.hasPlugin('parallax')
            && parallaxEffects.deveAplicar();
    },

    /* Aplica data-background (e o par data-background-webp, quando há) só quando o
       elemento chega a 800px da viewport. Duas atribuições de propósito: a primeira,
       url() simples, é o fallback; a segunda, image-set() com type(), só "pega" em
       navegador que a entende — nos outros o valor inválido é descartado e o JPEG fica.
       Sem IntersectionObserver, aplica na hora (o comportamento de antes). */
    lazyBackground: (el) => {
        const jpg = el.dataset.background;
        if (!jpg) return;
        const webp = el.dataset.backgroundWebp;
        const aplicar = () => {
            const src = assetManager.versionedPath(jpg);
            el.style.backgroundImage = `url(${src})`;
            if (webp) {
                el.style.backgroundImage = `image-set(url(${assetManager.versionedPath(webp)}) type('image/webp'), url(${src}) type('image/jpeg'))`;
            }
        };
        if (!('IntersectionObserver' in window)) {
            aplicar();
            return;
        }
        const io = new IntersectionObserver((entradas) => {
            if (entradas.some((e) => e.isIntersecting)) {
                io.disconnect();
                aplicar();
            }
        }, { rootMargin: '800px 0px' });
        io.observe(el);
    },

    setupPhoto: () => {
        const $elem = $('.parallax-photo[data-background]');

        // Verificar se existem elementos com parallax
        if (!$elem.length) return;

        // Verificar se estamos em um dispositivo compatível com parallax
        const isDesktop = $(window).width() > 1199;
        const shouldApplyParallax = parallaxEffects.deveAplicar();

        // Aplicar configuração adequada a cada elemento
        $elem.each(function () {
            const element = $(this);

            // Imagem de fundo só perto da tela (antes baixava na inicialização: 130 KB
            // abaixo da dobra na home, medido no PageSpeed de 2026-09-23).
            parallaxEffects.lazyBackground(this);

            // Aplicar parallax ou configuração estática
            if (shouldApplyParallax && assetManager.hasPlugin('parallax') && typeof element.parallax === 'function') {
                try {
                    element.parallax("50%", 0.1);
                } catch (error) {
                    console.warn('Parallax indisponível para este elemento.', error);
                    element.css("background-attachment", "scroll");
                }
            } else if (utils.prefersReducedMotion() || (isDesktop && device.tablet())) {
                /* Sem o parallax do JS, o background-attachment:fixed de style.css ainda
                   segura o fundo enquanto o conteúdo rola — o mesmo efeito visual por
                   outro caminho. Para movimento reduzido, ancorar o fundo é parte do
                   conserto, não detalhe. */
                element.css("background-attachment", "scroll");
            }
        });
    }
};

/**
 * Gerenciador de galeria
 */
const galleryManager = {
    init: () => {
        const $gallery = $('#gallery-masonry');

        if ($gallery.length && assetManager.hasPlugin('masonry') && assetManager.hasPlugin('magnificPopup')) {
            $gallery.masonry({
                columnWidth: ".grid_sizer",
                itemSelector: ".masonry_col",
                transitionDuration: (device.tablet() || device.mobile() ? 0 : "1s"),
            });

            // Inicialização do Magnific Popup
            $gallery.magnificPopup({
                delegate: '.magnific-zoom-gallery',
                type: 'image',
                gallery: {
                    enabled: true
                },
                image: {
                    titleSrc: 'title'
                },
                fixedContentPos: true
            });
        }
    }
};

/**
 * Gerenciador de seções do site
 * (heróis agora são nativos: imagem inline via heroBackgroundStyle() no PHP)
 */
const sectionManager = {
    home: async () => {
        if (parallaxEffects.shouldLoadPlugin()) {
            await assetManager.ensureOptionalBundle('parallax');
        }

        // Herói estático (2026-08-17): a rotação por crossfade (heroRotate) foi removida —
        // pré-carregava 3 slides (171 KB) e repintava a tela cheia a cada 6 s sem dado de
        // conversão a favor. A 1ª (e única) imagem segue vindo do CSS server-side
        // (heroBackgroundStyle + preload no header), que é o que sustenta o LCP.
        if (window.__heroInt) { clearInterval(window.__heroInt); window.__heroInt = null; }

        parallaxEffects.setupPhoto();
    },

    'meu-dentista': () => { },

    // /tratamentos não tem init próprio desde 2026-09-24: o modal de artigo
    // ([data-md_artigo] → /ajax/artigos/tratamentos/*.js) não tinha gatilho no markup
    // e saiu junto com os 14 arquivos.

    // Rota de 2º nível pelo CAMINHO: o href inteiro contava a `/` de `?q=dor/inchaço` ou de um
    // `utm_term` como nível de rota e trocava a página no beacon de contato (2026-10-31).
    consultorios: () => {
        // Unidade específica
        if (window.location.pathname.split('/').filter(Boolean).length > 1) {
            section_page = null;
        }
    },

    dicas: async () => {
        dicasMetricas.init();
        if (window.location.pathname.split('/').filter(Boolean).length > 1) {
            section_page = null;
        }

        const hasDicasCarousel = $('#dicas-carousel').length > 0;

        if (hasDicasCarousel && !assetManager.hasPlugin('owlCarousel')) {
            await assetManager.ensureOptionalBundle('carousel');
        }

        if (parallaxEffects.shouldLoadPlugin()) {
            await assetManager.ensureOptionalBundle('parallax');
        }

        parallaxEffects.setupPhoto();

        if (hasDicasCarousel && !initializeDicasCarousel(2)) {
            await assetManager.ensureOptionalBundle('carousel');
            initializeDicasCarousel(2);
        }
    },

    // /agendamento não tem init próprio desde 2026-08-22: o botão "Sou Paciente"
    // (modal de login → /vc/) virou link de WhatsApp, que o whatsapp.js já trata.

    ortodontia: () => specialPages.init(),
    botox: () => specialPages.init(),
    "toxina-botulinica": () => specialPages.init(),
    'carga-imediata': () => specialPages.init(),
    // /implantes-dentarios não precisa de inicialização ou plugins opcionais.
    alectomia: () => specialPages.init(),
    bichectomia: () => specialPages.init(),
    'o-seu-dentista': () => specialPages.init(),

    ouvidoria: async () => {
        if (parallaxEffects.shouldLoadPlugin()) {
            await assetManager.ensureOptionalBundle('parallax');
        }

        modal_odonto.ouvidoria();
        // Configuração do parallax
        parallaxEffects.setupPhoto();
    },

    'politica-de-privacidade': () => specialPages.init()
};

/**
 * Configuração de páginas especiais
 */
const specialPages = {
    init: async () => {
        if (parallaxEffects.shouldLoadPlugin()) {
            await assetManager.ensureOptionalBundle('parallax');
        }

        if ($('#gallery-masonry').length && (!assetManager.hasPlugin('masonry') || !assetManager.hasPlugin('magnificPopup'))) {
            await assetManager.ensureOptionalBundle('gallery');
        }

        const hasDicasCarousel = $('#dicas-carousel').length > 0;

        if (hasDicasCarousel && !assetManager.hasPlugin('owlCarousel')) {
            await assetManager.ensureOptionalBundle('carousel');
        }

        // Configuração do parallax
        parallaxEffects.setupPhoto();

        // Inicialização da galeria
        galleryManager.init();

        if (hasDicasCarousel && !initializeDicasCarousel(1)) {
            await assetManager.ensureOptionalBundle('carousel');
            initializeDicasCarousel(1);
        }
    }
};

/**
 * Gerenciador de navegação
 */
const navigationManager = {
    menu: $('#main-menu'),
    requestId: 0,
    updateMetadata: (metadata) => {
        if (!metadata || typeof metadata.title !== 'string' || typeof metadata.description !== 'string') {
            throw new Error('Metadados da página ausentes');
        }
        const canonical = new URL(metadata.canonical);
        if (canonical.protocol !== 'https:' || canonical.hostname !== 'odontoluz.com') {
            throw new Error('Canonical inválido');
        }
        const setMeta = (attribute, key, value) => {
            let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
            if (!element) {
                element = document.createElement('meta');
                element.setAttribute(attribute, key);
                document.head.appendChild(element);
            }
            element.setAttribute('content', String(value ?? ''));
        };
        document.title = metadata.title;
        let link = document.head.querySelector('link[rel="canonical"]');
        if (!link) {
            link = document.createElement('link');
            link.setAttribute('rel', 'canonical');
            document.head.appendChild(link);
        }
        link.setAttribute('href', canonical.href);
        setMeta('name', 'title', metadata.title);
        setMeta('name', 'description', metadata.description);
        setMeta('name', 'robots', metadata.robots);
        setMeta('name', 'apple-mobile-web-app-title', metadata.title);
        const fields = { title: metadata.title, description: metadata.description, url: canonical.href,
            type: metadata.ogType, image: metadata.ogImage, 'image:width': metadata.ogWidth,
            'image:height': metadata.ogHeight, 'image:type': metadata.ogMime, 'image:alt': metadata.ogAlt };
        Object.entries(fields).forEach(([key, value]) => setMeta('property', 'og:' + key, value));
        // O head anterior pode conter schema de artigo/unidade. Não o atribuir à nova página.
        document.head.querySelectorAll('script[type="application/ld+json"]').forEach((node) => node.remove());
    },
    announce: (message, busy) => {
        const main = document.getElementById('page-main');
        if (main) main.setAttribute('aria-busy', String(busy));
        let status = document.getElementById('navigation-status');
        if (!status) {
            status = document.createElement('div');
            status.id = 'navigation-status';
            status.className = 'sr-only';
            status.setAttribute('role', 'status');
            status.setAttribute('aria-live', 'polite');
            document.body.appendChild(status);
        }
        status.textContent = message;
    },

    // Seção cujo conteúdo está NA TELA. O voltar/avançar depois de uma navegação AJAX só
    // troca a URL; ver o popstate em init().
    secaoNaTela: null,
    secaoDaUrl: (pathname) => String(pathname).replace(/^\/+/, '').split('/')[0] || 'home',

    // atualizarHistorico=false na carga inicial: a URL já é a da página. Empilhar a mesma URL
    // duplicava a entrada do histórico (o "voltar" pedia dois toques) e APAGAVA a query string
    // (?utm_…, ?gclid=, ?pg=) antes de o GTM, injetado no load, ler a URL — o page_view do GA4
    // saía com dl sem os utm (medido em 2026-09-24). O skip-link também nasce certo do servidor.
    setActiveItem: (url, preserveNestedPath = false, atualizarHistorico = true) => {
        if (!url) return null;

        const normalizedUrl = String(url).replace(/^\/+/, '');
        const formattedUrl = (normalizedUrl === 'home' ? '/' : `/${normalizedUrl}`);
        const formattedUrlWithoutSlash = formattedUrl.replace(/^\//, '');
        navigationManager.menu
            .find('.active')
            .removeClass('active')
            .end()
            .find(`[data-page="${formattedUrl}"], [data-page="${formattedUrlWithoutSlash}"]`)
            .parent('li')
            .addClass('active');

        if (!atualizarHistorico) {
            return null;
        }

        if (preserveNestedPath && formattedUrl !== '/' && window.location.pathname.startsWith(`${formattedUrl}/`)) {
            return null;
        }

        // O skip-link (include/header.php) carrega a URL da página no href, porque o
        // <base href> do <head> faz um "#page-main" solto resolver contra a base e sair
        // da página. Ele nasce com a URL de ENTRADA; sem esta linha ficaria congelado
        // nela e, depois de navegar por AJAX, o atalho de teclado levaria o visitante de
        // volta à página onde ele entrou (achado em 2026-08-22).
        const skip = document.querySelector('a.skip-link');
        if (skip) {
            skip.setAttribute('href', formattedUrl + '#page-main');
        }

        window.history.pushState(
            formattedUrl,
            $(`[data-page="${formattedUrl}"], [data-page="${formattedUrlWithoutSlash}"]`).attr('title'),
            formattedUrl
        );
        navigationManager.secaoNaTela = navigationManager.secaoDaUrl(formattedUrl);
        return null;
    },

    setupAjaxNavigation: () => {
        navigationManager.menu.find('.nav').off('click.ajaxNavigation').on('click.ajaxNavigation', 'a', async function (e) {
            const a = $(this);
            const hashlink = a.attr("href");
            const target = a.prop('target');
            const data = a.data('page');
            const body = $('body');
            // Preserve abrir em outra aba, downloads, âncoras e links fora do roteador.
            if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || (e.button != null && e.button !== 0)
                || target || this.hasAttribute('download') || !data) return;
            const destination = new URL(hashlink, window.location.href);
            if (destination.origin !== window.location.origin || destination.hash || destination.search) return;
            e.preventDefault();
            const requestId = ++navigationManager.requestId;

            if (!target && window.location.pathname !== hashlink) {
                try {
                    body.addClass('loader');
                    navigationManager.announce('Carregando página…', true);

                    const response = await fetch(`/ajax/page.php?page=${encodeURIComponent(data)}`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded'
                        }
                    });

                    if (!response.ok) throw new Error('Falha HTTP ao carregar página');
                    const responseData = await response.json();
                    if (requestId !== navigationManager.requestId) return;

                    if (responseData.type === 'success') {
                        await assetManager.ensureForSection(responseData.page);
                        if (requestId !== navigationManager.requestId) return;

                        navigationManager.updateMetadata(responseData.metadata);

                        navigationManager.setActiveItem(data);

                        unidadeBarra.destroy();
                        // Atualizar conteúdo
                        // Injeta dentro do <main id="page-main"> (index.php), não no
                        // #content: o <header> da navegação é irmão do main e não pode
                        // ficar dentro dele. Remover também scripts/schema irmãos das sections.
                        // HTML antigo sem main precisa de uma carga completa, preservando o header.
                        if (!$('#page-main').length) {
                            window.location.href = hashlink;
                            return;
                        }
                        $('#page-main').off().empty().append(responseData.conteudo);

                        // Recarregar a página
                        await pageLoader.load(responseData.page, { rolarAoTopo: true });
                        if (requestId !== navigationManager.requestId) return;
                        const main = document.getElementById('page-main');
                        if (main) {
                            main.setAttribute('tabindex', '-1');
                            main.focus({ preventScroll: true });
                        }
                        navigationManager.announce('Página carregada: ' + document.title, false);
                        return;
                    } else {
                        return window.location.href = hashlink;
                    }
                } catch (erro) {
                    if (requestId !== navigationManager.requestId) return;
                    console.error('Erro ao carregar página:', erro);
                    return window.location.href = hashlink;
                } finally {
                    if (requestId === navigationManager.requestId) {
                        body.removeClass('loader');
                        document.getElementById('page-main')?.setAttribute('aria-busy', 'false');
                    }
                }

                return false;
            } else {
                body.removeClass('loader');
                navigationManager.announce('', false);
            }
        });
    },

    init: (section, preserveNestedPath = false) => {
        navigationManager.setActiveItem(section, preserveNestedPath, false);
        navigationManager.secaoNaTela = navigationManager.secaoDaUrl(window.location.pathname);

        // Voltar/avançar para OUTRA seção depois de uma navegação AJAX: a URL muda e o conteúdo
        // não (não há como remontar a seção anterior) — recarregar traz a página certa do
        // servidor. Mesma seção (âncora #…, /meu-dentista/{slug} do modal.js) não recarrega.
        window.addEventListener('popstate', () => {
            if (navigationManager.secaoDaUrl(window.location.pathname) !== navigationManager.secaoNaTela) {
                window.location.reload();
            }
        });

        return navigationManager.setupAjaxNavigation();
    },

    // Rolagem suave das âncoras .smoothscroll. O deslocamento compensa o que cobre o topo:
    // o logo em .top-bar, a barra fixa até 1199px; acima disso a barra é lateral (0).
    setupSmoothScroll: () => {
        const offset = $("body").hasClass("top-bar")
            ? -$("#logo").innerHeight()
            : ($(window).width() > 1199 ? 0 : -$('.sidebar-menuicon').height());

        $(".smoothscroll").smoothScroll({
            speed: 1000,
            offset,
            afterScroll: function (opts) {
                const link = this;
                const concluir = () => {
                    if ($(link).hasClass("havehash")) {
                        window.location.href = $(link).attr("href");
                    }
                };
                /* content-visibility (custom-style.css, 20260923.14): seção fora da tela tem
                   altura ESTIMADA, e as que a animação atravessou ganham a real no caminho, então
                   o destino calculado no clique fica defasado. Passadas instantâneas recalculam
                   com as alturas reais até o alvo parar no lugar. Uma só não bastava no celular
                   (página longa de uma coluna): cada salto renderiza seções novas em volta do
                   destino, e o 1º clique em /implantes-dentarios parava ~1.000 px antes de
                   #unidades a 360 px (2026-10-17). A altura real só aparece no quadro seguinte,
                   daí o rAF duplo. Teto de passadas e parada quando a rolagem não anda mais
                   (alvo perto do fim da página) impedem o laço. */
                const alvo = opts && opts.scrollTarget && $(opts.scrollTarget)[0];
                if (!alvo) {
                    concluir();
                    return;
                }
                const quadro = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));
                const ajustar = (passada, yAnterior) => {
                    const erro = alvo.getBoundingClientRect().top + (opts.offset || 0);
                    if (passada >= 6 || Math.abs(erro) <= 2 || window.scrollY === yAnterior) {
                        concluir();
                        return;
                    }
                    const y = window.scrollY;
                    $.smoothScroll($.extend({}, opts, {
                        speed: 0,
                        afterScroll: () => quadro(() => ajustar(passada + 1, y))
                    }));
                };
                quadro(() => ajustar(0, null));
            }
        });
    },

    // Configuração de tooltips do menu
    setupMenuTooltips: () => {
        $('.use-tooltips').tooltip('destroy');
        if ($(window).width() > 1199 && !device.tablet() && !device.mobile()) {
            return $(".use-tooltips").tooltip({
                placement: "right"
            });
        }
    },

    // Estado do sticky nav mobile. null = ainda não decidido nesta carga.
    _menuiconOculto: null,

    // Configura a navegação mobile
    setupMobileNavigation: () => {
        /* Roda a CADA scroll e a cada resize. Até 2026-09-10 consultava o DOM e escrevia
           estilo (.hide()/.show()) toda vez, mesmo sem mudança de estado — e o handler de
           scroll do jquery.parallax lê offset().top no mesmo evento. Escrita seguida de
           leitura de layout força reflow síncrono a cada quadro de rolagem: é o "Reflow
           forçado" que o PageSpeed aponta. Agora só escreve na TRANSIÇÃO de estado.

           Cachear o estado é seguro porque nada mais oculta/exibe .sidebar-menuicon
           (verificado em 2026-09-10: as duas únicas chamadas são as daqui; :810 só lê
           .height() e :850 só mexe em transition). Se algum dia outro código passar a
           escondê-lo, este cache precisa ser invalidado junto. */
        // Navegação precisa permanecer alcançável por teclado em paisagem e zoom.
        // Ocultar no topo deixava os controles fora da ordem de foco até rolar.
        const ocultar = false;

        if (ocultar === navigationManager._menuiconOculto) {
            return;
        }

        navigationManager._menuiconOculto = ocultar;
        $('.sidebar-menuicon')[ocultar ? 'hide' : 'show']();
    }
};

/**
 * Gerenciador de UI e interações
 */
const uiManager = {
    setupDeviceSpecificStyles: () => {
        // Desabilitar transições em dispositivos móveis
        if (device.tablet() || device.mobile()) {
            // Layout para direita
            $("#main-menu, .sidebar-menuicon, #content-wrapper").css("transition", "none");
            // Menu principal
            $("#main-menu ul li a").css("transition", "none");
            $("#main-menu ul li a:hover:after").css("transition", "none");
            // de-icon
            $(".de-icon, .de-icon i").css("transition", "none");
            // Photo-item
            $(".photo-item-preview > .autocrop-image, .photo-item-preview > img").css("transition", "none");
            $(".photo-item-overlay").css("transition", "none");
        }
    },

    setupMenuInteractions: () => {
        /* Um unico interruptor para os dois hamburgueres. Ate 2026-08-21 cada um abria o
           menu por um mecanismo proprio e sem estado comum: o #desktop-menu desliza o
           <header> inteiro (.moveto-right revela o #main-menu, que mora em left:-300
           dentro dele) e o #mobile-menu so dava slideToggle no display do painel. Abrir
           por um e fechar pelo outro deixava o painel escondido COM o header deslocado —
           a barra ciano de 60px parada a 300px sobre o texto, sem UI para desfazer. Nao
           era so teoria: a regra de alvo de toque (pointer:coarse) revelava o #mobile-menu
           no iPad em paisagem e o dedo caia justamente nele. O CSS ja foi corrigido; isto
           aqui garante que, se algum dia o botao reaparecer, ele nao produza estado morto. */
        const desktopLayout = () => $(window).width() > 1199;

        const toggleDesktopMenu = (trigger) => {
            $(trigger).toggleClass("open");
            $("header, #content-wrapper").toggleClass("moveto-right");
            $("#blocker").toggleClass("visible");
        };

        // Menu Desktop
        $("#desktop-menu").on("click", function (e) {
            e.preventDefault();
            toggleDesktopMenu(this);
        });

        // Menu Mobile
        $("#mobile-menu").on("click", function (e) {
            e.preventDefault();
            if (desktopLayout()) {
                return toggleDesktopMenu("#desktop-menu");
            }
            $(this).toggleClass("open");
            $("#main-menu").stop().slideToggle(350);
        });

        /* O wordmark tambem abre o menu (2026-09-10, a pedido do Ronan): ate entao
           #logo-link era href="javascript:null;" e nao fazia NADA, num alvo de 106px de
           largura no topo do celular. Delega ao #mobile-menu em vez de repetir o toggle
           aqui — o comentario no topo desta funcao existe porque dois mecanismos para o
           mesmo menu ja produziram estado morto (painel escondido com o header
           deslocado), e um terceiro faria de novo. O #mobile-menu ja roteia sozinho para
           o toggle de desktop acima de 1199px, entao isto funciona nos dois layouts. */
        $("#logo-link").on("click", function (e) {
            e.preventDefault();
            $("#mobile-menu").trigger("click");
        });

        // Detectar redimensionamento da janela para ajustar menu
        $(window).resize(function () {
            if ($(window).width() <= 1199) {
                if ($("#desktop-menu").hasClass("open")) {
                    $("#desktop-menu").removeClass("open");
                    $("header, #content-wrapper").removeClass("moveto-right");
                }
                if (!$("#mobile-menu").hasClass("open")) {
                    $("#main-menu").css("display", "none");
                }
                $("#blocker").removeClass("visible");
            } else {
                if ($("#mobile-menu").hasClass("open")) {
                    $("#mobile-menu").removeClass("open");
                }
                $("#main-menu").css("display", "block");
            }
        });

        // Esconder menus dropdown
        $("#main-menu li").each(function () {
            if ($(this).find("> ul").length > 0) {
                return $(this).children(".child").hide();
            }
        });

        // Toggle menus dropdown
        $('#main-menu li:has(">ul")').on("click", "a[href^='#']", function () {
            $(this).parent().find(".child").stop(true, true).slideToggle(500, "easeOutExpo");
            return false;
        });

        // Esconder menu ao clicar
        const hideMainMenu = () => {
            if ($(window).width() <= 1199) {
                $("#main-menu").hide();
                $("#mobile-menu").removeClass("open");
            } else {
                setTimeout(function () {
                    $("#blocker").removeClass("visible");
                    $("#desktop-menu").removeClass("open");
                    $("header, #content-wrapper, #main-menu").removeClass("moveto-right");
                }, 100);
            }
        };

        $("#main-menu li").click(hideMainMenu);

        // Esconder menu ao clicar em ícones
        $("#navbar-menuicon .smoothscroll, #navbar-menuicon .popup-link").click(function () {
            if ($("#desktop-menu").hasClass("open") || $("#mobile-menu").hasClass("open")) {
                return hideMainMenu();
            }
        });

        // Menu Bootstrap
        const navMain = $(".navbar-collapse");
        navMain.on("click", "a", null, function () {
            if ($(this).attr("href") !== "#") {
                return navMain.collapse('hide');
            }
        });

        // Adicionar bloqueador para conteúdo
        $("#content").append('<div id="blocker"></div>');
        $("#blocker").on("click", function () {
            $(this).removeClass("visible");
            $("#desktop-menu").removeClass("open");
            $("header, #content-wrapper, #main-menu").removeClass("moveto-right");
        });
    },

    updateBodyClass: () => {
        if (window.innerWidth > 1199) {
            if (!$("body").hasClass("fullsize")) {
                return $("body").addClass("fullsize");
            }
        } else {
            return $("body").removeClass("fullsize");
        }
    },

    setupFullHeight: () => {
        const fheight = $(window).height();

        if (window.innerWidth > 1199 || (device.landscape() && window.innerWidth <= 640)) {
            // Preloader, Slider, Location Height
            $(".fullpage").css({
                "height": fheight,
                "margin-top": 0
            });

            // Outros elementos
            if ($("body").hasClass("top-bar")) {
                $(".fullsize .fullheight").css("height", fheight - $("#logo").innerHeight()).css("margin-top", 0);
            } else {
                $(".fullsize .fullheight").css({
                    "height": fheight,
                    "margin-top": 0
                });
            }
        } else {
            // Mobile/tablet: a altura do herói é do CSS (#odontologia.fullpage em
            // css/custom-style.css: min-height calc(100svh - var(--menu-h)) + margin-top).
            // Setar height/margin-top aqui, depois do jQuery (defer), encolhia o herói
            // em 60px e o empurrava 60px para baixo já com a página pintada — 0,073 de
            // CLS em cada página com herói (Lighthouse mobile, 2026-08-17). Só limpa o
            // inline que o ramo desktop pode ter deixado numa mudança de largura.
            $(".fullpage").css({
                "height": "",
                "margin-top": ""
            });

            // Outros elementos
            $(".fullheight").css({
                "height": "auto",
                "margin-top": 0
            });
        }
    }
};

/**
 * Fachada de vídeo (sections/dicas.php e a grade de sections/_treatment.php): cria o <iframe> do YouTube só no primeiro
 * clique. Antes o embed vinha no HTML e custava ~1,5 MB por visita no desktop, além
 * de empurrar o corpo do post enquanto carregava (CLS 0,656 medido).
 *
 * Listener DELEGADO no document, de propósito: funciona no primeiro load e na
 * navegação AJAX sem depender da cadeia de init (pageLoader), e não acumula
 * listeners por navegação. O elemento é um <button>, então Enter/Espaço já
 * disparam 'click' — teclado funciona sem código extra.
 */
document.addEventListener('click', (e) => {
    const alvo = e.target instanceof Element ? e.target.closest('[data-yt-facade]') : null;
    if (!alvo) return;

    const id = (alvo.dataset.ytFacade || '').replace(/[^A-Za-z0-9_-]/g, '');
    if (!id) return;

    const iframe = document.createElement('iframe');
    // autoplay: o clique é a intenção explícita do usuário (não é autoplay de página).
    // rel=0: no fim, sugere só vídeos do próprio canal, não os de outra clínica.
    // playsinline=1: no iPhone toca dentro da página em vez de abrir a tela cheia.
    iframe.src = 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&rel=0&playsinline=1';
    iframe.title = alvo.getAttribute('aria-label') || 'Vídeo';
    iframe.allow = 'accelerometer; autoplay; encrypted-media; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.setAttribute('style', 'position:absolute;top:0;left:0;width:100%;height:100%;border:0');

    const caixa = document.createElement('div');
    caixa.className = 'yt-embed';
    // Mesmas medidas inline da fachada: o bundle e assincrono e o iframe entra depois
    // do load — sem isto a caixa nasceria sem proporcao reservada. Proporcao e largura
    // vem da propria fachada: o post e 16:9/560px, a grade de tratamento e 4:3/480px.
    const proporcao = alvo.style.aspectRatio || '16/9';
    const largura = alvo.style.maxWidth || '560px';
    const raio = alvo.style.borderRadius ? ';border-radius:' + alvo.style.borderRadius : '';
    caixa.setAttribute('style', 'position:relative;display:block;width:100%;max-width:' + largura + ';margin:0 auto;aspect-ratio:' + proporcao + ';overflow:hidden' + raio);
    caixa.appendChild(iframe);
    alvo.replaceWith(caixa);
    iframe.focus();
});

/**
 * FAQ curto da home (sections/home.php): o "Ver mais" tira o `hidden` das perguntas
 * guardadas e some. Delegado no document pelo mesmo motivo da fachada acima: vale no
 * primeiro load e na navegação AJAX sem entrar na cadeia de init. O foco vai para a
 * primeira pergunta revelada — o botão que o teclado estava usando deixa de existir.
 */
document.addEventListener('click', (e) => {
    const botao = e.target instanceof Element ? e.target.closest('[data-faq-mais]') : null;
    if (!botao) return;

    const lista = document.getElementById(botao.getAttribute('aria-controls') || '');
    if (!lista) return;

    const ocultas = lista.querySelectorAll(':scope > [hidden]');
    ocultas.forEach((el) => el.removeAttribute('hidden'));
    botao.setAttribute('aria-expanded', 'true');
    (botao.closest('p') || botao).hidden = true;
    ocultas[0]?.querySelector('.panel-title a')?.focus();
});

/**
 * Carregador de página
 */
// Reserva a altura real da barra, incluindo quebra de texto e safe area. A variável fica no <html> porque quem reserva o
// espaço é o rodapé (irmão do #content-wrapper, fora dele): no wrapper o rodapé não a herdava (2026-10-23).
// Um observador por página; não mantém referências ao conteúdo removido pelo SPA.
const unidadeBarra = {
    observer: null,
    visao: null,
    update: null,
    destroy() {
        this.observer?.disconnect();
        this.visao?.disconnect();
        if (this.update) window.removeEventListener('resize', this.update);
        document.documentElement.style.removeProperty('--unidade-barra-altura');
        this.observer = this.visao = this.update = null;
    },
    init() {
        this.destroy();
        const bar = document.querySelector('.unidade-barra');
        if (!bar) return;
        this.update = () => {
            const height = Math.ceil(bar.getBoundingClientRect().height);
            document.documentElement.style.setProperty('--unidade-barra-altura', `${height}px`);
        };
        this.update();
        if (typeof ResizeObserver === 'function') {
            this.observer = new ResizeObserver(this.update);
            this.observer.observe(bar);
        } else {
            window.addEventListener('resize', this.update);
        }
        this.initRolagem(bar);
    },
    // data-mostrar="rolagem" (piloto nos posts, 2026-10-20): a barra só aparece com TODOS os elementos do seletor
    // data-esconde-em fora da tela (no post: cabeçalho, ponte e par de botões do fim), para nunca empilhar chamadas.
    // Sem IntersectionObserver ela fica sempre visível.
    initRolagem(bar) {
        if (bar.dataset?.mostrar !== 'rolagem') return;
        const alvos = [...document.querySelectorAll(bar.dataset.escondeEm || '[data-barra-esconde]')];
        if (typeof IntersectionObserver !== 'function' || alvos.length === 0) {
            bar.classList.add('is-visivel');
            return;
        }
        const naTela = new Set();
        this.visao = new IntersectionObserver((entradas) => {
            entradas.forEach((en) => (en.isIntersecting ? naTela.add(en.target) : naTela.delete(en.target)));
            bar.classList.toggle('is-visivel', naTela.size === 0);
        });
        alvos.forEach((alvo) => this.visao.observe(alvo));
    }
};

const pageLoader = {
    load: async (sectionPage, { rolarAoTopo = false } = {}) => {
        unidadeBarra.init();
        const resolvedSection = assetManager.normalizeSection(sectionPage || 'home');

        // Bundle opcional de seção (owl/masonry/parallax) que não fique pronto em 4 s
        // rejeitava aqui e abortava TODO o resto — inclusive modal_odonto.int(), que liga
        // o botão "Agendar". Sem CTA e sem erro visível (revisão 2026-08-17). Agora a
        // falha é logada e a inicialização segue; a seção degrada só no que dependia do bundle.
        try {
            await assetManager.ensureForSection(resolvedSection);
        } catch (error) {
            console.warn(`Bundle da seção indisponível — seguindo sem ele: ${resolvedSection}`, error);
        }

        whatsapp.init();

        // Só na troca de página do SPA. Na carga inicial o navegador já sabe onde ficar
        // (#âncora, #:~:text=, restauração do F5/voltar) e rolar ao topo aqui desfazia isso
        // ~1 s depois: /ortodontia#agendar abria e voltava ao topo (FE-JS-01, medido em Chrome
        // real; docs/relatorios/2026-09-29-revisao-completa.md).
        if (rolarAoTopo) {
            $.smoothScroll({
                top: 0,
                speed: 1000
            });
        }

        uiManager.setupFullHeight();

        // Carregar seção específica — erro do script da seção não pode derrubar o
        // modal_odonto.int() logo abaixo (mesma lógica do try/catch acima).
        if (sectionManager[resolvedSection]) {
            try {
                await sectionManager[resolvedSection]();
            } catch (error) {
                console.warn(`Erro ao iniciar a seção ${resolvedSection}`, error);
            }
        }

        uiManager.updateBodyClass();
        navigationManager.setupSmoothScroll();
        modal_odonto.int();
        connectionManager.updateStatus(utils.isOnline());
    }
};

/**
 * Inicialização da página
 */
$(document).ready(function () {
    /* Movimento reduzido: desliga o motor de animação do jQuery. O slideToggle do menu, o
       slideUp/Down do painel de info e os fades do modal rodam em timer próprio e não são
       alcançados pelo @media de custom-style.css. Com $.fx.off o jQuery salta direto para
       o estado final e AINDA dispara os callbacks — mesma semântica do .01ms do CSS, que
       preserva o fim e descarta só o percurso. */
    const mqlMovimento = typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;

    if (mqlMovimento) {
        $.fx.off = mqlMovimento.matches;
        const aoMudarMovimento = (e) => { $.fx.off = e.matches; };
        // addListener é o caminho do Safari < 14, que ainda aparece no parque instalado.
        if (typeof mqlMovimento.addEventListener === 'function') {
            mqlMovimento.addEventListener('change', aoMudarMovimento);
        } else if (typeof mqlMovimento.addListener === 'function') {
            mqlMovimento.addListener(aoMudarMovimento);
        }
    }

    // Configuração de UI e dispositivos
    uiManager.setupDeviceSpecificStyles();
    uiManager.setupMenuInteractions();
    uiManager.updateBodyClass();
    uiManager.setupFullHeight();

    // Configuração de eventos de redimensionamento
    $(window).resize(function () {
        uiManager.updateBodyClass();
        uiManager.setupFullHeight();

        navigationManager.setupMobileNavigation();
        navigationManager.setupSmoothScroll();
        navigationManager.setupMenuTooltips();
    });

    /* Scroll coalescido em um quadro. Sem isto, o handler roda em toda notificação de
       scroll — dezenas por segundo no mobile — e cada execução lê layout ($(window).width(),
       .scrollTop()). Com rAF, no máximo uma execução por quadro pintado. */
    let scrollAgendado = false;
    $(window).scroll(function () {
        if (scrollAgendado) {
            return;
        }

        scrollAgendado = true;
        window.requestAnimationFrame(function () {
            scrollAgendado = false;
            navigationManager.setupMobileNavigation();
        });
    });

    window.requestAnimationFrame(function () {
        const initialSection = section_page || 'home';

        // Carregamento inicial
        pageLoader.load(initialSection);
        navigationManager.setupMobileNavigation();
        navigationManager.init(initialSection, true);
        navigationManager.setupMenuTooltips();
    });
});

// Métricas editoriais (GA4), só com consentimento e sem telefone ou identificador do visitante.
// Página com busca (?q=) não carrega o GTM (js/modulo/tag-loader.js) nem traz .dicas-medicao:
// a busca se conta no servidor, sem o termo (registrarBuscaSite(), include/functions/functions.php).
// page_title/page_location/page_referrer ficam os da página: fixá-los aqui contaminava as
// dimensões de página e de origem de todo evento (artigo virava "Dicas de saúde bucal").
// Os eventos da CARGA (init) só entram na fila do dataLayer: o tag-loader injeta o GTM no `load`, e
// forçá-lo aqui o trazia no DOMReady, na conta do LCP de /dicas e dos posts, para todo visitante que
// já aceitou os cookies (revisão de 2026-10-31). Só a interação (clique, guia aberto) carrega na hora.
const dicasMetricas = (() => {
    const permitido = () => privacyManager.readDecision() === 'granted';
    const emitir = (evento, params = {}, carregar = true) => {
        try {
            if (!permitido() || typeof window.gtag !== 'function') return false;
            if (carregar && typeof window.odlCarregarGtm === 'function') window.odlCarregarGtm();
            window.gtag('event', evento, { send_to: 'G-GV4HEZBJ28', transport_type: 'beacon', ...params });
            return true;
        } catch (_) { return false; }
    };
    const init = () => {
        if (!permitido()) return;
        const lista = document.querySelector('.dicas-medicao');
        if (lista && lista.dataset.dicasMedido !== '1') {
            const params = {
                dicas_total: Number(lista.dataset.dicasTotal) || 0,
                dicas_pagina: Number(lista.dataset.dicasPagina) || 1,
                dicas_tema: lista.dataset.dicasTema || 'todos'
            };
            if (emitir('dicas_resultados', params, false)) {
                lista.dataset.dicasMedido = '1';
                if (params.dicas_tema !== 'todos') emitir('dicas_filtro', params, false);
            }
        }
        const post = document.querySelector('[data-dica-atual-tema]');
        if (post && post.dataset.dicasMedido !== '1' && emitir('dicas_artigo_aberto', { dicas_tema: post.dataset.dicaAtualTema }, false)) {
            post.dataset.dicasMedido = '1';
            try { sessionStorage.setItem('odl_dica_lida', '1'); } catch (_) { /* armazenamento indisponível */ }
        }
    };
    document.addEventListener('click', (e) => {
        const guia = e.target.closest('[data-dicas-guia-link]');
        if (guia) emitir('dicas_guia_click', { dicas_guia: guia.dataset.dicasGuiaLink, dicas_passo: Number(guia.dataset.dicasPasso) || 0 });
        const card = e.target.closest('[data-dica-posicao]');
        if (card) emitir('dicas_card_click', {
            dicas_posicao: Number(card.dataset.dicaPosicao) || 0,
            dicas_tema: card.dataset.dicaTema
        });
        const contato = e.target.closest('a[data-whatsapp="true"],a[href^="tel:"]');
        if (contato) {
            try {
                if (permitido() && sessionStorage.getItem('odl_dica_lida') === '1') {
                    emitir('dicas_contato', { dicas_canal: contato.matches('a[href^="tel:"]') ? 'telefone' : 'whatsapp' });
                }
            } catch (_) { /* medição não interfere no contato */ }
        }
    });
    document.addEventListener('toggle', (e) => {
        if (e.target.matches('details[data-dicas-guia]') && e.target.open) {
            emitir('dicas_guia_aberto', { dicas_guia: e.target.dataset.dicasGuia });
        }
    }, true);
    return { init };
})();

// Eventos ao carregar a página completa
$(window).on("load", function () {
    // Inicializar gerenciador de conexão
    connectionManager.init();

    // Inicializar gerenciador de privacidade
    privacyManager.init();
});
